import React, { useState, useRef, useEffect } from "react";
import { FiBell as BellIcon, FiUserPlus, FiMessageSquare, FiSettings } from "react-icons/fi";
import { FaTag, FaRegCalendarAlt, FaRegFileAlt, FaCircle, FaInfo, FaCloudUploadAlt } from "react-icons/fa";
import { MdOutlineSecurityUpdateGood } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import moment from "moment";
import { getNotifications, markAllAsRead, markAsRead } from "../../Redux/Slice/NotificationSlice";
import socket from "../../socket";
import { toast } from "react-toastify";


const NotificationDropdown = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const loginData = Cookies.get("loginData");
    const PersonData = loginData ? JSON.parse(loginData) : null;
    const userId = PersonData?.data?._id;

    const { notifications } = useSelector((state) => state.Notification);
    const { isdata } = useSelector((state) => state.auth);
    const isMain = isdata?.data?.IsMain;
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [filterType, setFilterType] = useState("All");
    const notificationRef = useRef(null);

    const notificationPrefs = isdata?.data?.NotificationPreferences || PersonData?.data?.NotificationPreferences || [];
    const prefsRef = useRef(notificationPrefs);

    useEffect(() => {
        prefsRef.current = notificationPrefs;
    }, [notificationPrefs]);

    useEffect(() => {

        if (userId) {
            socket.on("notification", (data) => {
                console.log("New live notification received:", data);

                // Avoid duplicate/self notifications for actions performed by the current user
                const userName = PersonData?.data?.Name;
                const isSelfMessage = userName && data.message && (
                    data.message.toLowerCase().includes(`by ${userName.toLowerCase()}`) ||
                    data.message.toLowerCase().startsWith(`${userName.toLowerCase()} has applied`) ||
                    data.message.toLowerCase().startsWith(`${userName.toLowerCase()} advance leave`) ||
                    data.message.toLowerCase().startsWith(`senior ${userName.toLowerCase()} has assigned`) ||
                    data.message.toLowerCase().startsWith(`${userName.toLowerCase()} has assigned`)
                );

                if (data.senderId === userId || isSelfMessage) {
                    console.log("Skipping toast/notification because current user is the sender/creator.");
                    dispatch(getNotifications({ userId }));
                    return;
                }

                // Check if this notification is for this user
                const userRole = PersonData?.data?.Role;
                const isTargeted = data.targetEmployee === userId ||
                    (data.targetRoles && data.targetRoles.includes(userRole));

                if (!isTargeted) return;

                // PREFERENCE CHECK: Ensure only notifications selected in Notification Settings trigger live popup/toast and list refresh
                const isOwner = userRole?.toLowerCase() === "owner";
                if (isOwner) {
                    const currentPrefs = prefsRef.current || [];
                    if (currentPrefs.length > 0) {
                        if (!currentPrefs.includes("All")) {
                            const notifType = (data.type || "").toLowerCase();
                            const notifTitle = data.title || "";

                            const matchesType = currentPrefs.some(p => {
                                const pLower = p.toLowerCase();
                                return pLower === notifType ||
                                    (p === "GstR & Compliance" && data.type === "GSTR Compliance") ||
                                    (p === "GSTR Compliance" && data.type === "GstR & Compliance");
                            });
                            const matchesTitle = currentPrefs.includes(notifTitle);
                            const matchesSpecial = (currentPrefs.includes("Customer Support") && notifTitle === "New Customer Requirement");

                            if (!matchesType && !matchesTitle && !matchesSpecial) {
                                console.log(`[Notification] Skipped "${notifTitle}" (${data.type}) - not in owner's selected Notification Preferences.`);
                                return;
                            }
                        }
                    } else {
                        // Default fallback for owner if no preferences saved yet
                        const defaultOwnerAllowedTitles = [
                            "Task Assigned",
                            "Task Accepted",
                            "Task Solved",
                            "Task Completed",
                            "Task Verified",
                            "New Task Generated",
                            "New Developer Task",
                            "New Customer Requirement"
                        ];
                        if (data.title && !defaultOwnerAllowedTitles.includes(data.title)) {
                            console.log(`[Notification] Skipped "${data.title}" - not in default allowed titles.`);
                            return;
                        }
                    }
                }

                dispatch(getNotifications({ userId }));

                const isLiveToastEnabled = localStorage.getItem("liveToastNotification") !== "false";
                if (isLiveToastEnabled) {
                    // Check browser Notification permission
                    if (typeof window !== "undefined" && "Notification" in window) {
                        if (Notification.permission === "granted") {
                            // Show native browser notification
                            const showLocalNotification = (title, options) => {
                                if ("serviceWorker" in navigator) {
                                    navigator.serviceWorker.ready
                                        .then((registration) => {
                                            registration.showNotification(title, options);
                                        })
                                        .catch((err) => {
                                            console.error("ServiceWorker showNotification failed, using fallback:", err);
                                            try {
                                                new Notification(title, options);
                                            } catch (e) {
                                                console.error("Fallback Notification constructor failed:", e);
                                            }
                                        });
                                } else {
                                    try {
                                        new Notification(title, options);
                                    } catch (e) {
                                        console.error("Notification constructor failed:", e);
                                    }
                                }
                            };

                            showLocalNotification(data?.title || "New Notification", {
                                body: data?.message || "You have a new update.",
                                icon: "/logo192.png",
                                badge: "/logo192.png",
                                vibrate: [100, 50, 100],
                            });
                        } else if (Notification.permission === "default") {
                            // Request permission and show notification if granted
                            Notification.requestPermission().then((permission) => {
                                if (permission === "granted") {
                                    try {
                                        new Notification(data?.title || "New Notification", {
                                            body: data?.message || "You have a new update.",
                                            icon: "/logo192.png"
                                        });
                                    } catch (e) {
                                        console.error("Notification failed:", e);
                                    }
                                } else {
                                    // Fallback to toast if permission is denied after request
                                    toast.info(
                                        <div className="flex flex-col gap-1">
                                            <strong className="text-[14px] font-bold text-gray-800">{data?.title || "New Notification"}</strong>
                                            <span className="text-[12px] text-gray-600 line-clamp-2">{data?.message || "You have a new update."}</span>
                                        </div>,
                                        {
                                            position: "bottom-right",
                                            autoClose: 5000,
                                            hideProgressBar: false,
                                            closeOnClick: true,
                                            pauseOnHover: true,
                                            draggable: true,
                                            progress: undefined,
                                            icon: "🔔",
                                            style: { borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }
                                        }
                                    );
                                }
                            });
                        } else {
                            // Fallback to toast if browser notifications are blocked/denied
                            toast.info(
                                <div className="flex flex-col gap-1">
                                    <strong className="text-[14px] font-bold text-gray-800">{data?.title || "New Notification"}</strong>
                                    <span className="text-[12px] text-gray-600 line-clamp-2">{data?.message || "You have a new update."}</span>
                                </div>,
                                {
                                    position: "bottom-right",
                                    autoClose: 5000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                    draggable: true,
                                    progress: undefined,
                                    icon: "🔔",
                                    style: { borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }
                                }
                            );
                        }
                    } else {
                        // Fallback to toast if window.Notification is not supported
                        toast.info(
                            <div className="flex flex-col gap-1">
                                <strong className="text-[14px] font-bold text-gray-800">{data?.title || "New Notification"}</strong>
                                <span className="text-[12px] text-gray-600 line-clamp-2">{data?.message || "You have a new update."}</span>
                            </div>,
                            {
                                position: "bottom-right",
                                autoClose: 5000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                                progress: undefined,
                                icon: "🔔",
                                style: { borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }
                            }
                        );
                    }
                }
            });

            return () => {
                socket.off("notification");
            };
        }
    }, [userId, dispatch, PersonData?.data?.Role]);


useEffect(() => {
    if (userId) {
        dispatch(getNotifications({ userId }));
        // const interval = setInterval(() => {
        //     dispatch(getNotifications({ userId }));
        // }, 15000);
        // return () => clearInterval(interval);
    }
}, [userId, dispatch]);

useEffect(() => {
    const handleClickOutside = (event) => {
        if (notificationRef?.current && !notificationRef?.current.contains(event?.target)) {
            setIsNotificationOpen(false);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

const toggleOpen = () => {
    setIsNotificationOpen(!isNotificationOpen);
};

const handleClearAll = () => {
    if (userId) {
        dispatch(markAllAsRead(userId));
    }
};

const handleNotificationClick = (notif) => {
    // Mark individual notification as read so it disappears from the list
    if (userId) {
        dispatch(markAsRead({ notificationId: notif._id, userId }));
    }

    if (notif.type === "Task" && notif.relatedId) {
        const userRole = PersonData?.data?.Role;

        // Seniors/Owner/Management use /InhouseRnd, Juniors and others use /CustomerReq
        const seniorRoles = ["owner", "Web Senior Developer", "Delphi Senior Developer", "management"];
        const prefix = seniorRoles.includes(userRole) ? "/InhouseRnd" : "/CustomerReq";

        navigate(`${prefix}/CustomerrequustDetail/${notif.relatedId}`);
        setIsNotificationOpen(false);
    }

    if (notif.type === "Leave") {
        const userRole = PersonData?.data?.Role;
        if (userRole === "owner") {
            navigate("/OwnerLeaveInbox");
        } else if (["Web Junior Developer", "Delphi Junior Developer", "Data Analyst", "Junior Customer Support", "inhouse"].includes(userRole)) {
            navigate("/MyRequest");
        } else {
            // Seniors, management, etc.
            if (isMain) {
                navigate("/GeneralManagerRequest");
            } else {
                navigate("/ProjectManagerRequest");
            }
        }
        setIsNotificationOpen(false);
    }
};

const getIconInfo = (type) => {
    switch (type) {
        case "Task":
            return { icon: <FaRegFileAlt size={16} className="text-orange-500" />, iconBg: "bg-gradient-to-br from-amber-50 to-orange-50/70 border border-orange-100/50" };
        case "Employee":
            return { icon: <FiUserPlus size={16} className="text-emerald-600" />, iconBg: "bg-gradient-to-br from-teal-50 to-emerald-50/70 border border-emerald-100/50" };
        case "Leave":
            return { icon: <FaRegCalendarAlt size={16} className="text-rose-500" />, iconBg: "bg-gradient-to-br from-pink-50 to-rose-50/70 border border-rose-100/50" };
        case "WhatsApp":
            return { icon: <FiMessageSquare size={16} className="text-blue-500" />, iconBg: "bg-gradient-to-br from-cyan-50 to-blue-50/70 border border-blue-100/50" };
        case "OCR/Scan":
            return { icon: <FaRegFileAlt size={16} className="text-indigo-500" />, iconBg: "bg-gradient-to-br from-indigo-50 to-purple-50/70 border border-indigo-100/50" };
        case "Client":
            return { icon: <FaTag size={16} className="text-purple-500" />, iconBg: "bg-gradient-to-br from-purple-50 to-fuchsia-50/70 border border-purple-100/50" };
        case "Update":
            return { icon: <FaCloudUploadAlt size={16} className="text-sky-500" />, iconBg: "bg-gradient-to-br from-blue-50 to-sky-50/70 border border-sky-100/50" };
        case "GSTR Compliance":
            return { icon: <MdOutlineSecurityUpdateGood size={18} className="text-violet-500" />, iconBg: "bg-gradient-to-br from-violet-50 to-purple-50/70 border border-violet-100/50" };
        default:
            return { icon: <FaInfo size={14} className="text-slate-500" />, iconBg: "bg-gradient-to-br from-slate-50 to-slate-100/70 border border-slate-200/50" };
    }
};

const juniorRoles = ["Web Junior Developer", "Delphi Junior Developer", "Data Analyst", "Junior Customer Support", "inhouse"];
const isJunior = juniorRoles.includes(PersonData?.data?.Role);

const restrictedTitles = ["New Daily Report", "Leave Request Submitted"];

const accessibleNotifications = notifications?.filter(notif => {
    const isRestricted = restrictedTitles.some(title => notif.title?.includes(title));
    return !(isRestricted && !isJunior);
}) || [];

const unreadCount = accessibleNotifications.filter(n => n.isUnread).length || 0;

const getRelativeTime = (date) => {
    const now = moment();
    const createdAt = moment(date);
    const diffMinutes = now.diff(createdAt, 'minutes');
    const diffHours = now.diff(createdAt, 'hours');
    const diffDays = now.diff(createdAt, 'days');

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes} min`;
    if (diffHours < 24) return `${diffHours} hour`;
    return `${diffDays} day`;
};

const categories = ["All", "Task", "Employee", "Leave", "OCR/Scan", "Client", "GstR & Compliance"];
const userRole = PersonData?.data?.Role;

const ownerBaseCategories = ["All"];
const ownerSelectedCats = notificationPrefs.includes("All")
    ? ["Task", "Employee", "Leave", "WhatsApp", "OCR/Scan", "Client", "Update", "GSTR Compliance"]
    : notificationPrefs.filter(p => !["Unread", "Read"].includes(p));

const currentCategories = userRole === "owner"
    ? [...new Set([...ownerBaseCategories, ...ownerSelectedCats])]
    : (isJunior ? ["All", "Task", "Leave"] : categories);

const filteredNotifications = accessibleNotifications.filter(notif => {
    if (filterType === "All") return true;
    if (userRole === "owner") {
        return notif.type === filterType || notif.title === filterType || (filterType === "Customer Support" && notif.title === "New Customer Requirement");
    }
    if (filterType === "Customer Support") return notif.title === "New Customer Requirement";
    if (filterType === "Task Assigned" || filterType === "Task Verified") return notif.title === filterType;
    return notif.type === filterType;
}).slice(0, 25); // Limit to 25 to prevent React DevTools "Maximum call stack size exceeded" error and improve performance

return (
    <>
        <style>{`
                @keyframes bell-shake {
                    0%, 100% { transform: rotate(0); }
                    10%, 30%, 50%, 70%, 90% { transform: rotate(8deg); }
                    20%, 40%, 60%, 80% { transform: rotate(-8deg); }
                }
                .animate-bell-shake {
                    animation: bell-shake 2s ease-in-out infinite;
                    transform-origin: top center;
                }
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>
        <div className="relative" ref={notificationRef}>
            <button
                onClick={toggleOpen}
                className="relative p-2.5 text-white/85 hover:text-white hover:bg-white/10 rounded-full transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none cursor-pointer flex items-center justify-center mr-1"
            >
                <BellIcon size={22} />
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 min-w-[18px] h-[18px] px-1 bg-gradient-to-r from-red-500 to-rose-600 border-2 border-[#133A69] rounded-full text-white text-[9px] font-bold flex items-center justify-center transform translate-x-1/4 -translate-y-1/4 shadow-md">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isNotificationOpen && (
                <div className="absolute right-[-70px] sm:right-0 mt-[14px] w-[320px] sm:w-[410px] bg-white rounded-2xl shadow-[0_20px_50px_rgba(15,23,42,0.15)] border border-slate-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-300">
                    {/* Soft Arrow indicator */}
                    <div className="absolute top-0 right-[92px] sm:right-[18px] w-4 h-4 bg-white border-t border-l border-slate-100/50 rotate-45 -translate-y-1/2 hidden sm:block"></div>

                    <div className="p-4 border-b border-slate-100 bg-white">
                        <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                                <h3 className="text-slate-800 font-extrabold text-[16px]">Notifications</h3>
                                {unreadCount > 0 && (
                                    <span className="px-2 py-0.5 bg-rose-50 text-rose-600 text-[10px] font-bold rounded-full border border-rose-100">
                                        {unreadCount} new
                                    </span>
                                )}
                                {userRole === "owner" && (
                                    <button
                                        onClick={() => {
                                            navigate("/notification?settings=true");
                                            setIsNotificationOpen(false);
                                        }}
                                        className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 transition-all active:scale-95 group cursor-pointer"
                                        title="Notification Settings"
                                    >
                                        <FiSettings size={16} className="group-hover:rotate-45 transition-transform duration-300" />
                                    </button>
                                )}
                            </div>
                            {accessibleNotifications.filter(n => n.isUnread).length > 0 && (
                                <button
                                    onClick={handleClearAll}
                                    className="text-[12px] font-bold text-blue-600 hover:text-blue-700 hover:underline transition-all cursor-pointer border-none bg-transparent"
                                >
                                    Clear All
                                </button>
                            )}
                        </div>

                        {/* Horizontal Categories Scrollable Pills */}
                        <div className="flex gap-1.5 overflow-x-auto pb-1 pt-0.5 -mx-1 px-1 no-scrollbar scroll-smooth">
                            {currentCategories.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setFilterType(cat)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer border-none ${filterType === cat
                                        ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/25'
                                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                                        }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                        {filteredNotifications?.length > 0 ? (
                            filteredNotifications.map((notif, index) => {
                                const { icon, iconBg } = getIconInfo(notif.type);
                                return (
                                    <div
                                        key={`${notif._id || 'notif'}-${index}`}
                                        onClick={() => handleNotificationClick(notif)}
                                        className={`p-3.5 flex gap-3.5 items-center transition-all duration-200 cursor-pointer border-b border-slate-50 last:border-0 relative select-none group ${notif.isUnread
                                            ? 'bg-blue-50/20 hover:bg-blue-50/40 border-l-4 border-l-blue-600 pl-3'
                                            : 'hover:bg-slate-50/80 border-l-4 border-l-transparent'
                                            }`}
                                    >
                                        <div className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-105 ${iconBg}`}>
                                            {icon}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex justify-between items-baseline mb-0.5">
                                                <h4 className={`text-xs font-bold truncate pr-3 ${notif.isUnread ? 'text-slate-800' : 'text-slate-700'}`}>
                                                    {notif.title}
                                                </h4>
                                                <span
                                                    className="text-[9px] font-semibold text-slate-400 shrink-0"
                                                    title={moment(notif?.createdAt).format('DD MMM YYYY, hh:mm A')}
                                                >
                                                    {getRelativeTime(notif?.createdAt)}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 leading-normal line-clamp-2 max-w-[240px] sm:max-w-[290px]">
                                                {notif.message}
                                            </p>
                                        </div>
                                        {notif.isUnread && (
                                            <div className="shrink-0 flex items-center justify-center pl-1.5 pr-0.5">
                                                <span className="w-1.5 h-1.5 bg-rose-500 rounded-full animate-pulse"></span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="p-8 text-center text-slate-400 text-xs italic font-medium">
                                No new notifications
                            </div>
                        )}
                    </div>
                    <div className="p-3 bg-slate-50/50 hover:bg-slate-50 text-center border-t border-slate-100 transition-colors duration-200">
                        <button
                            onClick={() => {
                                navigate("/notification");
                                setIsNotificationOpen(false);
                            }}
                            className="text-xs font-bold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 transition-all cursor-pointer border-none bg-transparent"
                        >
                            View All Notifications
                        </button>
                    </div>
                </div>
            )}
        </div>
    </>
);
};

export default NotificationDropdown;
